import React, { useState } from 'react';
import '../../App.css';
import shipraLogo from '../images/shipra-logo.png';
import packageLocationIcon from '../../assets/package-location.svg';

const ADMIN_ACCESS_CODE = process.env.REACT_APP_ADMIN_ACCESS_CODE || '';

const hasAdminAccess = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('code') === ADMIN_ACCESS_CODE;
};

const Icon = ({ name, size = 24, strokeWidth = 2, className = '' }) => {
  const paths = {
    truck: <><path d="M3 7h10v9H3z" /><path d="M13 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="18" r="2" /></>,
    globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m16 16 5 5" /></>,
    shield: <><path d="M12 3 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6z" /><path d="m8 12 2.5 2.5L16 9" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v6l4 2" /></>,
    headset: <><path d="M4 14v-2a8 8 0 0 1 16 0v2M4 14v4h4v-6H6a2 2 0 0 0-2 2ZM20 14v4h-4v-6h2a2 2 0 0 1 2 2ZM16 20c-1 1-2 1-4 1" /></>,
    clipboard: <><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V2h6v2M8 9h8M8 13h8M8 17h5" /></>,
    admin: <><circle cx="12" cy="8" r="4" /><path d="M5 21c0-4 2.8-7 7-7s7 3 7 7" /><path d="M17 12.5 19 14l2-1.5v4l-2 1.5-2-1.5z" /></>,
  };
  return <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
};

function Track({ onTrack, isLoading = false, apiError = '', onClearError, onOpenAdmin }) {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();
    const normalizedTrackingNumber = trackingNumber.trim();

    if (normalizedTrackingNumber.length < 2 || normalizedTrackingNumber.length > 255) {
      setError('Please enter a tracking number between 2 and 255 characters.');
      return;
    }

    setError('');
    onTrack(normalizedTrackingNumber);
  };

  return (
    <div className="shipra-track-page">
      <header className="site-header navbar navbar-expand-lg bg-white">
        <div className="container-fluid px-3 px-md-5">
          <a className="navbar-brand brand-wrap d-flex align-items-center" href="#top" aria-label="Shipra home">
            <img className="shipra-logo" src={shipraLogo} alt="Shipra Logistics" />
          </a>
          <button className="navbar-toggler border-0 shadow-none" type="button" data-bs-toggle="collapse" data-bs-target="#shipraNav" aria-controls="shipraNav" aria-expanded="false" aria-label="Toggle navigation">
            <span className="navbar-toggler-icon" />
          </button>
          <div className="collapse navbar-collapse" id="shipraNav">
            <nav className="navbar-nav mx-auto align-items-lg-center">
              <a className="nav-link active" href="#track">Track Order</a>
              <a className="nav-link" href="/">Services </a>
              <a className="nav-link" href="https://www.shipra.org/about-us/" target="_blank" rel="noreferrer">About Us</a>
              <a className="nav-link" href="https://www.shipra.org/contact/" target="_blank" rel="noreferrer">Contact Us </a>
            </nav>
            <div className="header-actions">
              <div className="dropdown language-menu">
                <button className="btn border-0 dropdown-toggle d-flex align-items-center gap-2" data-bs-toggle="dropdown"><Icon name="globe" size={25} /><b>EN</b></button>
                <ul className="dropdown-menu dropdown-menu-end border-0 shadow"><li><button className="dropdown-item">English</button></li> </ul>
              </div>
              {hasAdminAccess() && (
                <button
                  className="admin-nav-button"
                  type="button"
                  onClick={onOpenAdmin}
                  aria-label="Open shipment status admin review"
                >
                  <Icon name="admin" size={21} />
                  <span>Admin</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="hero-section" id="track">
          <div className="container hero-inner d-flex justify-content-center">
            <div className="tracking-card">
              <div className="package-badge">
                <img
                  className="package-badge-image"
                  src={packageLocationIcon}
                  alt="Package location"
                />
              </div>
              <h1><span>Track</span> Your Shipment</h1>
              <p className="subtitle">Get real-time updates on your package</p>
              <form className="form-content" onSubmit={handleSubmit} noValidate>
                <label htmlFor="trackingNumber" className="form-label">Enter Tracking Number (AWB / Reference) <em>*</em></label>
                <div className="input-group tracking-input">
                  <span className="input-group-text"><Icon name="search" size={22} /></span>
                  <input
                    id="trackingNumber"
                    className={`form-control${error || apiError ? ' is-invalid' : ''}`}
                    type="text"
                    value={trackingNumber}
                    onChange={(event) => {
                      setTrackingNumber(event.target.value);
                      setError('');
                      onClearError?.();
                    }}
                    placeholder="Enter Tracking Number (e.g. 12011408769045)"
                    aria-describedby="trackingHelp"
                    aria-invalid={Boolean(error || apiError)}
                    autoComplete="off"
                    minLength={2}
                    maxLength={255}
                    disabled={isLoading}
                  />
                </div>
                {error || apiError ? (
                  <div id="trackingHelp" className="form-text text-danger" role="alert">
                    {error || apiError}
                  </div>
                ) : (
                  <div id="trackingHelp" className="form-text">Enter your AWB or reference number</div>
                )}
                <button className="track-button btn w-100" type="submit" disabled={isLoading} aria-busy={isLoading}>
                  <Icon name={isLoading ? 'clock' : 'search'} size={28} />
                  <span>{isLoading ? 'Tracking shipment…' : 'Track Shipment'}</span>
                </button>
                <div className="security-note"><Icon name="shield" size={27} /><span>Your information is safe, secure and will only be used for tracking.</span></div>
              </form>
            </div>
          </div>
        </section>

        <section className="benefits-section" id="services">
          <div className="container-fluid px-lg-5">
            <div className="row g-0">
              <div className="col-12 col-sm-6 col-lg-3 benefit">
                <Icon name="shield" size={58} strokeWidth={1.65} />
                <h2>Secure &amp; Private</h2>
                <p>Your data is 100% safe<br />and encrypted</p>
              </div>

              <div className="col-12 col-sm-6 col-lg-3 benefit">
                <Icon name="clock" size={58} strokeWidth={1.65} />
                <h2>Real-time Updates</h2>
                <p>Get live status of your<br />shipment instantly</p>
              </div>

              <div className="col-12 col-sm-6 col-lg-3 benefit">
                <Icon name="headset" size={58} strokeWidth={1.65} />
                <h2>24/7 Support</h2>
                <p>We're here to help<br />you anytime</p>
              </div>

              <div className="col-12 col-sm-6 col-lg-3 benefit">
                <Icon name="clipboard" size={58} strokeWidth={1.65} />
                <h2>Stay Informed</h2>
                <p>Never miss an update<br />about your package</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer"><div className="container-fluid px-4 px-md-5 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3"><p>© 2026 Shipra Logistics. All rights reserved.</p><nav><a href="#privacy">Privacy Policy</a><span></span><a href="#terms">Terms &amp; Conditions</a></nav></div></footer>
    </div>
  );
}

export default Track;
